import React, { ForwardedRef, useCallback, useMemo } from 'react';

import { Text, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import gStyles from 'config/styles';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import { selectLayoutConfig } from 'store/config/selectors';
import { StreamTypes } from 'store/player/actions.types';
import { selectCatchupIconValid } from 'store/programs/selectors';
import { MixedItem } from 'store/railsBuilder/reducer.types';
import { startSentryTransaction } from 'store/sentry/actions';
import { SENTRY_TRANSACTIONS } from 'store/sentry/constants';
import { ReduxStore } from 'store/types';
import {
    selectCatchupCapability,
    selectEntitlements,
} from 'store/user/selectors';
import { isCapabilityValid } from 'store/utils';

import { ContentType } from 'services/data-manager/types';

import { isAssetVod } from 'utils/checkVodType';
import { COLOR } from 'utils/colors';
import getDeepLinkProviderValues from 'utils/deeplinks/getDeepLinkProviderValues';
import isFunction from 'utils/function';
import { makeTestDeeplinkTitle } from 'utils/getMockDeeplink';
import getOrdinal from 'utils/getOrdinary';
import getVodStripesExpiryDate from 'utils/getVodStripesExpiryDate';
import useDeeplinkItem from 'utils/hooks/useDeeplinkItem';
import useMixedItemParentalRating from 'utils/hooks/useMixedItemParentalRating';
import useProfileParentalRating from 'utils/hooks/useProfileParentalRating';
import needsToLockContent from 'utils/parentalControl/needsToLockContent';

import { isLive } from 'screens/Epg/utils/program';

import BaseTileImage from 'components/BaseTileImage';
import ContentMarkerIcons from 'components/ContentMarkerIcons';
import RibbonTouchable from 'components/CustomButtons/RibbonTouchable';
import Marker from 'components/Marker';
import {
    ImageContentType,
    ImageSize,
    ImageType,
} from 'components/NagraImage/types';
import { LockedContentCard } from 'components/Ribbon/components/LockedContentCard';
import ProgramLogo from 'components/Ribbon/components/ProgramLogo';
import StaticProgressBar from 'components/Ribbon/components/StaticProgressBar';
import { TileTitle } from 'components/Ribbon/components/TileTitle';
import { OnUavAnalyticsProps, StandardUavProps } from 'components/Ribbon/types';

import { PLACEHOLDER_ICON_CENTER } from '../../constants';
import gRibbonItemStyles from '../styles';

const {
    CARDS_TITLE_CONTENT_LOCKED,
    ACCESSIBILITY: {
        ACCESSIBILITY_COLLECTION_FIRST_ASSET,
        STRIPE: { ACCESSIBILITY_STRIPE_FIRST_ASSET },
    },
} = LANGUAGE_KEYS;

type Props<T = MixedItem> = {
    item: T;
    isCollection?: boolean;
    testID?: string;
    index?: number;
    assetContentType?: ContentType;
    onShowPinDialog?(): void;
    onSetParentalControlCheckedCallback?(
        callback: undefined | (() => void),
    ): void;
    totalNumberOfItems?: number;
} & OnUavAnalyticsProps &
    StandardUavProps;

const CatchupRibbon = (
    {
        item,
        onRailsSelection,
        onDeepLinkTriggered,
        onThirdPartyAppStart,
        isCollection = false,
        railId,
        templateId,
        testID,
        index = 0,
        assetContentType,
        onShowPinDialog,
        onSetParentalControlCheckedCallback,
        totalNumberOfItems,
    }: Props,
    forwardedRef?: ForwardedRef<View | undefined>,
) => {
    const {
        id,
        title,
        detailsPageParams,
        playbackType,
        type,
        contentType,
        channelId,
        season,
        episode,
        endTime,
        CUEndDate,
        vodProductIds,
        vodStartTime,
    } = item;
    const showBookmarkProgress = !!(
        assetContentType === ContentType.ContinueWatching &&
        item.position &&
        item.duration
    );
    const isVod = isAssetVod(playbackType);
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const profileParentalControlRating = useProfileParentalRating();

    const entitlements = useSelector(selectEntitlements);
    const { primary } = useSelector(selectLayoutConfig);

    const navigation =
        useNavigation<
            StackNavigationProp<RouteNavigationParams, SCREEN.DETAILS>
        >();

    const parentalRating = useMixedItemParentalRating(item);

    const isContentLocked = needsToLockContent(
        profileParentalControlRating,
        parentalRating,
    );

    const CUEndDateValid = useSelector((state: ReduxStore) =>
        selectCatchupIconValid(state, endTime, CUEndDate || undefined),
    );
    const catchupCapability = useSelector(selectCatchupCapability);

    const ableToPlayCatchup =
        isCapabilityValid(catchupCapability) && CUEndDateValid;
    const isSeries = !!item.seriesId;

    const { onPressPossibleDeeplinkItem } = useDeeplinkItem({
        item,
        ableToPlayCatchup,
        onDeepLinkTriggered,
        onThirdPartyAppStart,
        onRailsSelection,
        railId,
        templateId,
        playbackType,
        index,
    });

    const onPressCatchup = useCallback(() => {
        const { deeplinkProviderId, providerResourceId } =
            getDeepLinkProviderValues(item);
        onRailsSelection?.({
            id,
            playbackType: StreamTypes.catchup,
            index,
            deeplinkProviderId,
            providerResourceId,
        });

        if (detailsPageParams) {
            dispatch(
                startSentryTransaction(SENTRY_TRANSACTIONS.DetailPageRendering),
            );

            navigation.navigate(SCREEN.DETAILS, {
                railId,
                templateId,
                playbackType,
                type,
                ...detailsPageParams,
            });
        }
    }, [
        navigation,
        id,
        detailsPageParams,
        playbackType,
        type,
        railId,
        templateId,
        onRailsSelection,
        item,
        dispatch,
        index,
    ]);

    const handleOpenAsset = useCallback(() => {
        onPressPossibleDeeplinkItem(onPressCatchup);
    }, [onPressPossibleDeeplinkItem, onPressCatchup]);

    const handlePress = useCallback(() => {
        if (isContentLocked && isFunction(onShowPinDialog)) {
            if (isFunction(onSetParentalControlCheckedCallback)) {
                onSetParentalControlCheckedCallback(handleOpenAsset);
            }

            onShowPinDialog();
        } else {
            handleOpenAsset();
        }
    }, [
        isContentLocked,
        onShowPinDialog,
        handleOpenAsset,
        onSetParentalControlCheckedCallback,
    ]);

    const accessibilityLabel = useMemo(() => {
        if (isContentLocked) {
            return t(CARDS_TITLE_CONTENT_LOCKED);
        }

        let label = '';

        if (season) {
            label += `${t('HOVER_SEASON')}${season} `;
        }

        if (episode) {
            label += `${t('HOVER_EPISODE')}${episode} `;
        }

        label += `${title} `;

        const firstAssetKey = isCollection
            ? ACCESSIBILITY_COLLECTION_FIRST_ASSET
            : ACCESSIBILITY_STRIPE_FIRST_ASSET;
        const orderAssetKey = 'ACCESSIBILITY_STRIPE_%NUMBER%_ASSET';

        if (index === 0) {
            label += t(firstAssetKey, {
                number: totalNumberOfItems,
                interpolation: {
                    prefix: '%',
                    suffix: '%',
                    escapeValue: false,
                },
            });
        } else {
            label += t(orderAssetKey, {
                number: getOrdinal(index + 1),
                interpolation: {
                    prefix: '%',
                    suffix: '%',
                    escapeValue: false,
                },
            });
        }

        return label;
    }, [
        isContentLocked,
        season,
        episode,
        title,
        index,
        totalNumberOfItems,
        t,
        isCollection,
    ]);

    return (
        <RibbonTouchable
            ref={forwardedRef}
            testID={testID}
            metadataType={ContentType.Catchup}
            isCollection={isCollection}
            onPress={handlePress}
            railId={railId}
            accessibilityLabel={accessibilityLabel}
            RibbonContentComponent={
                isContentLocked ? (
                    <LockedContentCard channelId={channelId} />
                ) : (
                    <>
                        <BaseTileImage
                            style={gStyles.flex}
                            placeHolder={PLACEHOLDER_ICON_CENTER}
                            resizeMode="cover"
                            contentId={id}
                            contentType={
                                isSeries
                                    ? ImageContentType.EPISODE
                                    : contentType
                            }
                            imageType={
                                isSeries ? ImageType.EPISODE : ImageType.BANNER
                            }
                            width={ImageSize.LANDSCAPE.width}
                        />
                        <ProgramLogo channelId={channelId} />
                        <Marker
                            startTime={item.startTime}
                            endTime={item.endTime}
                            bgColor={COLOR.primary}
                            style={[
                                gRibbonItemStyles.marker,
                                // no progress bar visibile for live items in mixed, so now marker doesn't need so much bottom
                                isVod &&
                                    !isLive(item) &&
                                    gRibbonItemStyles.isOnLiveCard,
                            ]}
                            textStyle={gRibbonItemStyles.markerText}
                            expiryDate={
                                isVod
                                    ? getVodStripesExpiryDate(
                                          entitlements,
                                          vodProductIds,
                                      )
                                    : undefined
                            }
                            comingSoonDate={isVod ? vodStartTime : undefined}
                        />
                        <ContentMarkerIcons
                            parentalRating={parentalRating}
                            shouldDisplayCatchup={CUEndDateValid}
                            isOnLiveCard={showBookmarkProgress}
                            isLimitedToOne
                            isOnCard
                        />
                        {showBookmarkProgress && (
                            <StaticProgressBar
                                position={item.position}
                                duration={item.duration}
                                backgroundColor={primary}
                                contentType={ContentType.Mixed}
                            />
                        )}
                    </>
                )
            }
            RibbonInfoComponent={
                <TileTitle
                    title={
                        isContentLocked ? (
                            t(CARDS_TITLE_CONTENT_LOCKED)
                        ) : (
                            <>
                                {!!season && (
                                    <Text>{`${t(
                                        'HOVER_SEASON',
                                    )}${season} `}</Text>
                                )}
                                {!!episode && (
                                    <Text>
                                        {`${t('HOVER_EPISODE')}${episode} `}
                                    </Text>
                                )}
                                {makeTestDeeplinkTitle(item, title)}
                            </>
                        )
                    }
                />
            }
        />
    );
};

export default React.forwardRef(CatchupRibbon);
